import React, { useState, useEffect } from 'react';
import { notify } from '../utils/ToastUtils';
import moment from 'moment';
import ApiClient from "../ApiClient";
import { StatCardSkeleton } from "../components/PageSkeletonLoaders";
import { selectIsApiLoading } from "../stores/loadingSlice";
import { CalendarDays, Clock, ClipboardCheck, CalendarCheck, Search } from "lucide-react";
import {useSelector} from "react-redux"
import { selectProjects } from 'src/stores/projectSlice';
import TimesheetSummaryTable from '../components/TimesheetSummaryTable';
import { TextField, Select, MenuItem } from '@mui/material';
export default function TimeSheet() {
    const isApiLoading = useSelector(selectIsApiLoading);
    const projects = useSelector(selectProjects);

    const [stats, setStats] = useState({ totalDays: 0, totalHours: 0, totalProjects: 0 });
    const initialState = {
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        selectedProject: "All"
    }
    
    const [state, setState] = useState(initialState)    
    const [isDisabled, setIsDisabled] = useState(false)
    const [rawTimesheetData, setRawTimesheetData] = useState([]);

    function handleChange(evt) {
        const value = evt.target.value;
        setState({
            ...state,
            [evt.target.name]: value
        });
    }

    const sortedProjects = [...projects].sort((a, b) => {
        if (a.STATUS === "ACTIVE" && b.STATUS !== "ACTIVE") return -1;
        if (a.STATUS !== "ACTIVE" && b.STATUS === "ACTIVE") return 1;
        return 0;
    });

    const renderProjectOptions = () => {
        return sortedProjects?.map((proj) => (
            <MenuItem key={proj.PROJECT_CODE} value={proj.PROJECT_CODE} style={{ whiteSpace: "normal" }}>
                <div className="flex flex-col">
                    <div className="text-[14px] text-slate-800 break-words">{proj.PROJECT_NAME}</div>
                </div>
            </MenuItem>
        ));
    };

    const getTimesheet = async () => {
        if(state.startDate && state.endDate){
            if(new Date(state.startDate) <= new Date(state.endDate)){
                setIsDisabled(true)
                try{
                    const {data} = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/timesheet/getTimeSheetForEmp`,{
                        startDt:state.startDate,
                        endDt:state.endDate   
                    })

                    if(data.length > 0){
                        const filteredData = state.selectedProject === "All" ? data : data.filter(d => d.PROJECT_CODE === state.selectedProject);
                        setRawTimesheetData(filteredData);
                        
                        const uniqueDays = [...new Set(filteredData.map(d => d.WORK_DAY))];
                        const uniqueProjects = [...new Set(filteredData.map(d => d.PROJECT_CODE))];
                        const totalHours = filteredData.reduce((sum, d) => sum + d.WORK_HOURS, 0);

                        setStats({
                            totalDays: uniqueDays.length,
                            totalHours: totalHours,
                            totalProjects: uniqueProjects.length
                        });
                        
                        setIsDisabled(false)
                    }else {
                        setRawTimesheetData([]);
                        setIsDisabled(false)
                        notify.error("No matching records found")
                    }
                }catch(error){
                    console.log(error)
                    setIsDisabled(false)
                    let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                    notify.error(errmsg)
                }   
            }else notify.error("Start Date should be lesser than End Date")                     
        }else notify.error("Start Date & End Date are required")        
    }   

    useEffect(() => {
        getTimesheet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (    
        <div className="flex flex-col gap-4">
            {/* Filters Row */}
            <div className="bg-white p-4 border border-slate-200 rounded-2xl">
                <div className="grid grid-cols-1 sm:grid-cols-10 gap-4 items-end w-full">
                    <div className="w-full min-w-0 sm:col-span-5">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Project
                        </label>
                        <Select
                            name="selectedProject"
                            value={state.selectedProject}
                            className="w-full bg-white text-sm"
                            size="small"
                            onChange={handleChange}
                            MenuProps={{
                                PaperProps: {
                                    style: { maxHeight: 300, width: 250 },
                                },
                            }}
                            renderValue={(selected) => {
                                if (selected === "All") return "All Projects";
                                const proj = sortedProjects?.find(p => p.PROJECT_CODE === selected);
                                return proj ? proj.PROJECT_NAME : selected;
                            }}
                            sx={{
                                '& .MuiSelect-select': {
                                    paddingRight: '32px !important',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            <MenuItem value="All">
                                <div className="text-[14px] leading-snug break-words">All Projects</div>
                            </MenuItem>
                            {renderProjectOptions()}
                        </Select>
                    </div>

                    <div className="w-full min-w-0 sm:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            Start Date<span className="text-red-500">*</span>
                        </label>
                        <TextField
                            name="startDate"
                            type="date"
                            value={state.startDate}
                            className="w-full bg-white text-sm"
                            size="small"
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="w-full min-w-0 sm:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-1">
                            End Date<span className="text-red-500">*</span>
                        </label>
                        <TextField
                            name="endDate"
                            type="date"
                            value={state.endDate}
                            className="w-full bg-white text-sm"
                            size="small"
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="w-full sm:col-span-1 mt-2 sm:mt-0 flex">
                        <button
                            type="button"
                            onClick={getTimesheet}
                            disabled={isDisabled}
                            className="w-full text-sm h-[37px] px-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm flex items-center justify-center gap-2 whitespace-nowrap"
                            style={{ opacity: isDisabled ? "50%" : "100%" }}
                        >
                            {isDisabled ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <Search className="w-4 h-4" />
                                    Search
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Top Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {isApiLoading ? (
                    <>
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                        <StatCardSkeleton />
                    </>
                ) : (
                    <>
                        {(() => {
                            return (
                                <>
                                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                                <CalendarDays className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Total Days</h4>
                                <div className="flex items-end gap-1.5">
                                    <span className="text-xl font-bold text-slate-800 leading-none">{stats.totalDays}</span>
                                </div>
                                <p className="text-[10px] font-medium text-slate-400 mt-1">Days logged</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                <Clock className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Total Logged Hours</h4>
                                <div className="flex items-end gap-1.5">
                                    <span className="text-xl font-bold text-slate-800 leading-none">{stats.totalHours}</span>
                                    <span className="text-[10px] font-semibold text-slate-600 mb-0.5">hrs</span>
                                </div>
                                <p className="text-[10px] font-medium text-slate-400 mt-1">Across {stats.totalDays} working day{stats.totalDays === 1 ? '' : 's'}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                                <ClipboardCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Projects Worked On</h4>
                                <div className="flex items-end gap-1.5">
                                    <span className="text-xl font-bold text-slate-800 leading-none">{stats.totalProjects}</span>
                                </div>
                                <p className="text-[10px] font-medium text-slate-400 mt-1">Active project{stats.totalProjects === 1 ? '' : 's'}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <CalendarCheck className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Average Hours / Day</h4>
                                <div className="flex items-end gap-1.5">
                                    <span className="text-xl font-bold text-slate-800 leading-none">{stats.totalDays > 0 ? (stats.totalHours / stats.totalDays).toFixed(1) : 0}</span>
                                    <span className="text-[10px] font-semibold text-slate-600 mb-0.5">hrs</span>
                                </div>
                                <p className="text-[10px] font-medium text-slate-400 mt-1">Per working day</p>
                            </div>
                        </div>
                                </>
                            );
                        })()}
                    </>
                )}
            </div>

            <TimesheetSummaryTable 
                rawTimesheetData={rawTimesheetData} 
                projects={projects} 
                isApiLoading={isApiLoading}
                showFiltersToggle={false}
                filterState={state}
                onFilterChange={handleChange}
                onFilterSubmit={getTimesheet}
                isFilterDisabled={isDisabled}
            />
        </div>
    )
}
